import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Trash2, Phone, Mail } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { getAdminToken } from "@/lib/admin/session";
import { listReservations, updateReservation, deleteReservation } from "@/lib/admin/cms.functions";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/admin/prenotazioni/")({
  head: () => ({ meta: [{ title: "Richieste · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminReservations,
});

const STATI = [
  { value: "in_attesa", label: "In attesa" },
  { value: "confermata", label: "Confermata" },
  { value: "in_preparazione", label: "In preparazione" },
  { value: "pronta_per_ritiro", label: "Pronta" },
  { value: "completata", label: "Completata" },
  { value: "annullata", label: "Annullata" },
] as const;

type Stato = typeof STATI[number]["value"];

type Reservation = {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  telefono: string;
  prodotti: { name: string; qty?: string; slug?: string }[];
  note_cliente: string | null;
  admin_note: string | null;
  stato: Stato;
  data_ritiro_richiesta: string | null;
  created_at: string;
};

function AdminReservations() {
  const fetchList = useServerFn(listReservations);
  const update = useServerFn(updateReservation);
  const remove = useServerFn(deleteReservation);
  const [items, setItems] = useState<Reservation[]>([]);
  const [tab, setTab] = useState<Stato | "all">("in_attesa");
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetchList({ data: { token, stato: tab === "all" ? null : tab } });
      setItems(res.reservations as unknown as Reservation[]);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab]);

  async function setStato(id: string, stato: Stato) {
    const token = getAdminToken(); if (!token) return;
    try { await update({ data: { token, id, stato } }); toast.success("Stato aggiornato"); refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  async function setNote(id: string, admin_note: string) {
    const token = getAdminToken(); if (!token) return;
    try { await update({ data: { token, id, admin_note } }); toast.success("Nota salvata"); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  async function handleDelete(id: string) {
    if (!confirm("Eliminare questa prenotazione?")) return;
    const token = getAdminToken(); if (!token) return;
    try { await remove({ data: { token, id } }); toast.success("Eliminata"); refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  return (
    <AdminShell>
      <div className="space-y-6">
        <div><h1 className="font-display text-3xl">Prenotazioni</h1></div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Stato | "all")}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">Tutte</TabsTrigger>
            {STATI.map((s) => (<TabsTrigger key={s.value} value={s.value}>{s.label}</TabsTrigger>))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-500">
            Nessuna prenotazione in questo stato.
            <p className="text-xs mt-2">Le prenotazioni arriveranno qui quando i clienti useranno il carrello sul sito.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{r.nome} {r.cognome}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-1">
                      <a href={`tel:${r.telefono}`} className="flex items-center gap-1 hover:text-brand-red"><Phone className="h-3 w-3" />{r.telefono}</a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-brand-red"><Mail className="h-3 w-3" />{r.email}</a>
                      <span>Ricevuta: {new Date(r.created_at).toLocaleString("it-IT")}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.stato} onValueChange={(v) => setStato(r.id, v as Stato)}>
                      <SelectTrigger className="w-44 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATI.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
                {Array.isArray(r.prodotti) && r.prodotti.length > 0 && (
                  <ul className="mt-3 text-sm text-slate-700 list-disc list-inside">
                    {r.prodotti.map((p, i) => (<li key={i}>{p.name}{p.qty ? ` · ${p.qty}` : ""}</li>))}
                  </ul>
                )}
                {r.note_cliente && <div className="mt-2 text-sm text-slate-600 italic">"{r.note_cliente}"</div>}
                <div className="mt-3">
                  <Textarea
                    placeholder="Note interne (visibili solo all'admin)"
                    rows={2}
                    defaultValue={r.admin_note ?? ""}
                    onBlur={(e) => { if (e.target.value !== (r.admin_note ?? "")) setNote(r.id, e.target.value); }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}